import { deleteBuffer, getCached, putBuffer } from './BufferCache';
import { ensureStretchedBuffers } from './stretchedBuffers';
import { buildMonoPhrases, playDrumHit, playMonoPhrase, playNote, samplePlaybackRate } from './instrumentVoices';
import { sanitizePattern } from './patternModel';
import { getManifest, getSoundBuffer, loadProducerSounds, soundSignature } from './producerSounds';
import { isAudioEligible } from './classAudio';

const SAMPLE_RATE = 44100;
const renderedSignatures = new Map();
const inFlight = new Map();
const failedSignatures = new Map();

function offlineContext(channels, length, sampleRate) {
  const Context = globalThis.OfflineAudioContext || globalThis.webkitOfflineAudioContext;
  if (!Context) throw new Error('OfflineAudioContext is not supported by this browser');
  return new Context(channels, length, sampleRate);
}

export function drumHitSchedule(pattern, bpm) {
  const stepsPerBar = pattern.stepsPerBar === 12 ? 12 : 16;
  const stepSeconds = (240 / Number(bpm)) / stepsPerBar;
  const hits = [];
  for (const row of pattern.rows || []) {
    if (row.muted) continue;
    (pattern.steps?.[row.rowId] || []).forEach((velocity, step) => {
      if (!(velocity > 0)) return;
      const count = pattern.ratchets?.[row.rowId]?.[step] || 1;
      const swing = stepsPerBar === 16 && step % 2 === 1 ? Number(pattern.swing || 0) * stepSeconds * 0.5 : 0;
      for (let index = 0; index < count; index += 1) {
        hits.push({
          rowId: row.rowId,
          soundId: row.soundId,
          when: step * stepSeconds + swing + index * stepSeconds / count,
          velocity: Number(velocity) * (1 - index * 0.15),
          volume: Number(row.volume ?? 1),
          gain: Number(velocity) * (1 - index * 0.15) * Number(row.volume ?? 1),
        });
      }
    });
  }
  return hits;
}

function createImpulse(ctx, seconds) {
  const length = Math.max(1, Math.ceil(seconds * ctx.sampleRate));
  const impulse = ctx.createBuffer(2, length, ctx.sampleRate);
  for (let channel = 0; channel < 2; channel += 1) {
    const data = impulse.getChannelData(channel);
    for (let i = 0; i < length; i += 1) data[i] = (Math.random() * 2 - 1) * ((1 - i / length) ** 2);
  }
  return impulse;
}

function effectsGraph(ctx, pattern, bpm) {
  const input = ctx.createGain();
  const output = ctx.createGain();
  input.connect(output);
  const reverb = Number(pattern.reverb || 0);
  const echo = Number(pattern.echo || 0);
  const reverbDecay = reverb > 0 ? Math.min(2.5, Math.max(0.25, 2.5 * reverb)) : 0;
  const delaySeconds = 60 / Number(bpm) * 0.75;
  if (reverb > 0 && ctx.createConvolver) {
    const convolver = ctx.createConvolver();
    const wet = ctx.createGain();
    convolver.buffer = createImpulse(ctx, reverbDecay);
    wet.gain.value = reverb;
    input.connect(convolver); convolver.connect(wet); wet.connect(output);
  }
  if (echo > 0 && ctx.createDelay) {
    for (let n = 1; n <= 4; n += 1) {
      const delay = ctx.createDelay(4 * delaySeconds + 0.1);
      const wet = ctx.createGain();
      delay.delayTime.value = n * delaySeconds;
      wet.gain.value = echo * (0.35 ** n);
      input.connect(delay); delay.connect(wet); wet.connect(output);
    }
  }
  return { input, output };
}

function dryAudibleOverflow(clip, pattern, sounds, patternSeconds, bpm) {
  if (clip.patternType === 'drums') {
    return drumHitSchedule(pattern, bpm).reduce((max, hit) => {
      const buffer = sounds.get(hit.soundId);
      return Math.max(max, hit.when + Number(buffer?.duration || 0) - patternSeconds);
    }, 0);
  }
  if (clip.patternType === 'keys') {
    return (pattern.notes || []).reduce((max, note) => Math.max(max, (note.start + note.length) * 60 / bpm + pattern.release - patternSeconds), 0);
  }
  if (clip.patternType !== 'sampler') return 0;
  const buffer = sounds.get(pattern.sampleId);
  if (!buffer) return 0;
  return (pattern.notes || []).reduce((max, note) => {
    const rate = samplePlaybackRate({ pitch: note.pitch, rootNote: pattern.rootNote, tune: pattern.tune, fine: pattern.fine });
    const sourceDuration = buffer.duration / rate;
    const gatedDuration = pattern.gate ? note.length * 60 / bpm + pattern.release : sourceDuration;
    return Math.max(max, note.start * 60 / bpm + Math.min(sourceDuration, gatedDuration) - patternSeconds);
  }, 0);
}

function foldBuffer(rendered, exactLength, fadeTail, ctx) {
  const output = ctx.createBuffer(rendered.numberOfChannels, exactLength, rendered.sampleRate);
  const fadeSamples = Math.min(Math.round(0.05 * rendered.sampleRate), Math.max(0, rendered.length - exactLength));
  for (let channel = 0; channel < rendered.numberOfChannels; channel += 1) {
    const source = rendered.getChannelData(channel);
    const target = output.getChannelData(channel);
    for (let i = 0; i < source.length; i += 1) {
      let value = source[i];
      if (fadeTail && fadeSamples && i >= source.length - fadeSamples) value *= (source.length - i) / fadeSamples;
      target[i % exactLength] += value;
    }
  }
  return output;
}

export async function renderPatternBuffer(clip, bpm, suppliedSounds = null) {
  const pattern = sanitizePattern(clip.patternType, clip.pattern);
  const patternSeconds = pattern.patternBars * 240 / Number(bpm);
  const sounds = suppliedSounds || new Map();
  if (!suppliedSounds) {
    const ids = clip.patternType === 'drums' ? pattern.rows.map((row) => row.soundId).filter(Boolean) : [pattern.sampleId].filter(Boolean);
    await Promise.all(ids.map(async (id) => { const buffer = await getSoundBuffer(id); if (buffer) sounds.set(id, buffer); }));
  }
  const effectsTail = clip.patternType === 'drums' ? 0 : Math.max(pattern.reverb > 0 ? Math.min(2.5, 2.5 * pattern.reverb) : 0, pattern.echo > 0 ? 4 * (60 / Number(bpm) * 0.75) : 0);
  const release = clip.patternType === 'drums' ? 0 : pattern.release;
  const dryOverflow = Math.max(0, dryAudibleOverflow(clip, pattern, sounds, patternSeconds, bpm));
  const uncappedTail = Math.max(240 / Number(bpm), release + effectsTail, dryOverflow) + effectsTail;
  const tailSeconds = Math.min(8, uncappedTail);
  const wasCapped = uncappedTail > 8;
  const renderLength = Math.ceil((patternSeconds + tailSeconds) * SAMPLE_RATE);
  const exactLength = Math.round(patternSeconds * SAMPLE_RATE);
  const ctx = offlineContext(2, renderLength, SAMPLE_RATE);
  const graph = effectsGraph(ctx, pattern, bpm);
  graph.output.connect(ctx.destination);
  if (clip.patternType === 'drums') {
    for (const hit of drumHitSchedule(pattern, bpm)) {
      const buffer = sounds.get(hit.soundId);
      if (buffer) playDrumHit(ctx, graph.input, buffer, hit);
    }
  } else if (clip.patternType === 'keys') {
    const beatSeconds = 60 / Number(bpm);
    if (pattern.mono) {
      for (const phrase of buildMonoPhrases(pattern.notes, pattern.glide)) playMonoPhrase(ctx, graph.input, pattern, phrase, { beatSeconds });
    } else {
      for (const note of pattern.notes) playNote(ctx, graph.input, pattern, {
        pitch: note.pitch, when: note.start * beatSeconds, duration: note.length * beatSeconds, velocity: note.velocity,
      });
    }
  } else if (clip.patternType === 'sampler') {
    const buffer = sounds.get(pattern.sampleId);
    const beatSeconds = 60 / Number(bpm);
    if (buffer && pattern.mono) {
      for (const phrase of buildMonoPhrases(pattern.notes, pattern.glide)) playMonoPhrase(ctx, graph.input, { ...pattern, buffer }, phrase, { beatSeconds });
    } else if (buffer) for (const note of pattern.notes) playNote(ctx, graph.input, { ...pattern, buffer }, {
      pitch: note.pitch, when: note.start * beatSeconds, duration: note.length * beatSeconds, velocity: note.velocity,
    });
  }
  const rendered = await ctx.startRendering();
  return foldBuffer(rendered, exactLength, wasCapped, ctx);
}

async function signatureFor(clip, bpm) {
  await loadProducerSounds();
  const pattern = sanitizePattern(clip.patternType, clip.pattern);
  const ids = clip.patternType === 'drums' ? pattern.rows.map((row) => row.soundId).filter(Boolean) : [pattern.sampleId].filter(Boolean);
  return patternRenderSignature(clip.patternType, pattern, bpm, getManifest().version, ids.map(soundSignature));
}

export function patternRenderSignature(patternType, pattern, bpm, manifestVersion = 0, sounds = []) {
  const clean = sanitizePattern(patternType, pattern);
  return JSON.stringify([Number(bpm), clean.patternBars, clean.stepsPerBar ?? null, patternType, clean, manifestVersion, sounds]);
}

export async function ensurePatternBuffers(tracks, bpm) {
  // Library loops that need time stretch or pitch shift render alongside patterns, so every play,
  // export, load and import path that waits for patterns also waits for them.
  const stretched = ensureStretchedBuffers(tracks, bpm).catch((error) => console.warn('Stretch render failed:', error?.message || error));
  await loadProducerSounds();
  const clips = [];
  const seen = new Set();
  for (const track of tracks || []) for (const clip of isAudioEligible(track) ? (track.clips || []) : []) {
    if (clip.isPattern && !seen.has(clip.loop_id)) { seen.add(clip.loop_id); clips.push(clip); }
  }
  try {
  await Promise.all(clips.map(async (clip) => {
    const signature = await signatureFor(clip, bpm);
    if (getCached(clip.loop_id) && renderedSignatures.get(clip.loop_id) === signature) return getCached(clip.loop_id);
    if (failedSignatures.get(clip.loop_id) === signature) return null;
    const existing = inFlight.get(clip.loop_id);
    if (existing?.signature === signature) return existing.promise;
    const token = Symbol(clip.loop_id);
    const promise = (async () => {
      try {
        const clean = sanitizePattern(clip.patternType, clip.pattern);
        const ids = clip.patternType === 'drums' ? clean.rows.map((row) => row.soundId).filter(Boolean) : [clean.sampleId].filter(Boolean);
        const sounds = new Map();
        await Promise.all(ids.map(async (id) => { const buffer = await getSoundBuffer(id); if (buffer) sounds.set(id, buffer); }));
        const buffer = await renderPatternBuffer({ ...clip, pattern: clean }, bpm, sounds);
        const latest = inFlight.get(clip.loop_id);
        if (latest?.token === token && latest.signature === signature) {
          putBuffer(clip.loop_id, buffer);
          renderedSignatures.set(clip.loop_id, signature);
          failedSignatures.delete(clip.loop_id);
        }
        return buffer;
      } catch (error) {
        const latest = inFlight.get(clip.loop_id);
        if (latest?.token === token && latest.signature === signature) {
          deleteBuffer(clip.loop_id);
          renderedSignatures.delete(clip.loop_id);
          failedSignatures.set(clip.loop_id, signature);
        }
        throw error;
      } finally {
        if (inFlight.get(clip.loop_id)?.token === token) inFlight.delete(clip.loop_id);
      }
    })();
    inFlight.set(clip.loop_id, { signature, token, promise });
    return promise;
  }));
  } finally {
    await stretched;
  }
}

export function __resetPatternRenderForTests() {
  renderedSignatures.clear(); inFlight.clear(); failedSignatures.clear();
}
